import { useEffect, useMemo, useState } from 'react';
import { collection, doc, updateDoc, onSnapshot, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { auth, db } from '../../config/firebaseConfig';
import { getOnlineEmployeeIds, toMillis } from '../../utils/presence';
import useAccessText from './accessText';
import {
  buildPages,
  describePath,
  generateStrongPassword,
  getRoles,
  isActiveEmployee,
  isAdmin,
  isRestrictedAdmin,
  pageState,
} from './accessUtils';

// Cloud Function endpoints
const SET_PASSWORD_URL =
  'https://us-central1-habcare-8ecad.cloudfunctions.net/adminSetUserPassword';
const CREDENTIALS_EMAIL_URL =
  'https://us-central1-habcare-8ecad.cloudfunctions.net/sendAccountCredentialsEmailV2';

/**
 * Data and actions of Sayfa Yetkileri: the live employee list, presence (who
 * is online and on which page), the page-grant editor with its draft, saving,
 * the password change, messaging an online user, and the list filters. The
 * Firestore reads/writes and the Cloud Function calls are the old page's.
 */
const usePageAccess = () => {
  const { label, formatSince } = useAccessText();
  const allPages = useMemo(buildPages, []);

  const [allEmployees, setAllEmployees] = useState([]);
  // Pasif çalışanlar varsayılan olarak gizlenir; anahtar sayfanın tamamını
  // (liste, editör, admin/çevrimiçi pencereleri ve istatistikler) etkiler.
  const [showPassiveEmployees, setShowPassiveEmployees] = useState(false);
  const [loading, setLoading] = useState(true);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [listFilter, setListFilter] = useState('all'); // all | online | granted
  const [pageSearch, setPageSearch] = useState('');
  const [pageFilter, setPageFilter] = useState('all'); // all | on | off
  const [selectedId, setSelectedId] = useState(null);
  const [draftGrants, setDraftGrants] = useState([]); // working list of granted page paths
  const [saving, setSaving] = useState(false);
  // Kaydedilmemiş değişiklik varken başka çalışana geçilmek istendiğinde
  const [pendingSelection, setPendingSelection] = useState(null);
  // Çevrimiçi listesinden mesaj gönderme
  const [messageTarget, setMessageTarget] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // Şifre değiştirme penceresi
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [pwSaving, setPwSaving] = useState(false);
  const [pwError, setPwError] = useState('');

  // Sayfadaki tüm listeler bu türetilmiş liste üzerinden çalışır, böylece
  // anahtar tek noktadan hem paneli hem pencereleri etkiler.
  const employees = useMemo(
    () => (showPassiveEmployees ? allEmployees : allEmployees.filter(isActiveEmployee)),
    [allEmployees, showPassiveEmployees]
  );

  const passiveCount = useMemo(
    () => allEmployees.filter((emp) => !isActiveEmployee(emp)).length,
    [allEmployees]
  );

  // Yetkilendirmesi "admin" olan tüm çalışanlar
  const adminEmployees = useMemo(() => employees.filter(isAdmin), [employees]);

  // Yetkilendirmesi "Kısıtlı Admin" olan tüm çalışanlar
  const restrictedAdminEmployees = useMemo(() => employees.filter(isRestrictedAdmin), [employees]);

  // Çalışan listesi canlı dinlenir. Önceden bu liste 5 saniyede bir
  // getDocs(collection('Employees')) ile baştan çekiliyordu; her çekiş
  // koleksiyondaki HER dokümanı okuduğu için maliyet çalışan sayısıyla
  // çarpılıyordu (5 sn'de bir × N çalışan ≈ günde 17.280 × N okuma).
  // onSnapshot ilk yüklemede bir kez okur, sonrasında yalnızca değişen
  // dokümanları getirir — hem çok daha ucuz hem de anlık.
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'Employees'),
      (snap) => {
        // Pasifler de çekilir; gösterilip gösterilmeyeceğine "Pasifleri Göster"
        // anahtarı karar verir.
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) =>
          `${a.eName || ''} ${a.eSurname || ''}`.localeCompare(
            `${b.eName || ''} ${b.eSurname || ''}`,
            'tr'
          )
        );
        setAllEmployees(list);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching employees:', error);
        toast.error(label('toast.loadError'));
        setLoading(false);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Çevrimiçilik son nabzın yaşına bakar, bu yüzden yeni bir Firestore olayı
  // gelmese bile zamanla eskimeli. Saniye sayacı bunu sağlar.
  const [presenceNow, setPresenceNow] = useState(() => Date.now());
  useEffect(() => {
    const tick = setInterval(() => setPresenceNow(Date.now()), 15000);
    const handleVisibilityChange = () => {
      if (!document.hidden) setPresenceNow(Date.now());
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      clearInterval(tick);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const onlineUsers = useMemo(
    () => getOnlineEmployeeIds(employees, presenceNow),
    [employees, presenceNow]
  );

  // Online olan tüm çalışanlar
  const onlineEmployees = useMemo(
    () => employees.filter((emp) => onlineUsers.has(emp.id)),
    [employees, onlineUsers]
  );

  // Çevrimiçi kullanıcıların o an bulunduğu sayfa ve orada geçirdiği süre
  const onlineActivity = useMemo(() => {
    const map = {};
    onlineEmployees.forEach((emp) => {
      const page = describePath(emp.currentPath);
      const since = toMillis(emp.currentPathAt);
      map[emp.id] = {
        page,
        duration: since ? formatSince(presenceNow - since) : null,
      };
    });
    return map;
  }, [onlineEmployees, presenceNow, formatSince]);

  // Mesaj, projedeki mevcut Messenger ile aynı `messages` koleksiyonuna
  // yazılır; alıcı mesajı normal mesaj kutusunda görür ve cevaplayabilir.
  const sendMessage = async () => {
    const text = messageText.trim();
    if (!messageTarget || !text) return;

    const senderEmail = auth.currentUser?.email;
    const receiverEmail = messageTarget.eMail;

    if (!senderEmail) {
      toast.error(label('toast.noSession'));
      return;
    }
    if (!receiverEmail) {
      toast.error(label('toast.noReceiverEmail'));
      return;
    }

    setSendingMessage(true);
    try {
      await addDoc(collection(db, 'messages'), {
        senderEmail,
        receiverEmail,
        text,
        fileUrl: null,
        fileName: null,
        timestamp: serverTimestamp(),
        read: false,
      });
      toast.success(label('toast.messageSent', { name: `${messageTarget.eName || ''} ${messageTarget.eSurname || ''}`.trim() }));
      setMessageTarget(null);
      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(label('toast.messageError'));
    } finally {
      setSendingMessage(false);
    }
  };

  const openMessage = (emp) => {
    setMessageTarget(emp);
    setMessageText('');
  };

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedId) || null,
    [employees, selectedId]
  );

  const selectedRoles = useMemo(
    () => (selectedEmployee ? getRoles(selectedEmployee) : []),
    [selectedEmployee]
  );

  // Seçili çalışan listeden düşerse (pasifler gizlendiğinde veya kayıt
  // silindiğinde) editörde artık görünmeyen bir kişi açık kalmasın.
  useEffect(() => {
    if (!selectedId) return;
    if (!employees.some((e) => e.id === selectedId)) {
      setSelectedId(null);
      setDraftGrants([]);
    }
  }, [employees, selectedId]);

  const selectEmployee = (emp) => {
    if (!emp) {
      setSelectedId(null);
      setDraftGrants([]);
      return;
    }
    setSelectedId(emp.id);
    setDraftGrants(Array.isArray(emp.pageAccess) ? [...emp.pageAccess] : []);
    setPageSearch('');
  };

  const accessibleCount = (emp) => {
    const roles = getRoles(emp);
    const grants = Array.isArray(emp.pageAccess) ? emp.pageAccess : [];
    return allPages.filter(
      (p) => pageState(p, grants, emp.eMail, roles) !== 'none'
    ).length;
  };

  const toggleGrant = (page) => {
    setDraftGrants((prev) =>
      prev.includes(page.path)
        ? prev.filter((p) => p !== page.path)
        : [...prev, page.path]
    );
  };

  /** Adds every changeable page of a menu group, or removes them all when all are on. */
  const toggleGroup = (pages) => {
    if (!selectedEmployee) return;
    const free = pages.filter((page) => {
      const state = pageState(page, [], selectedEmployee.eMail, selectedRoles);
      return state === 'none';
    });
    if (free.length === 0) return;
    setDraftGrants((prev) => {
      const allOn = free.every((page) => prev.includes(page.path));
      if (allOn) return prev.filter((path) => !free.some((page) => page.path === path));
      return Array.from(new Set([...prev, ...free.map((page) => page.path)]));
    });
  };

  // Unsaved-change detection (order-insensitive)
  const originalGrants = useMemo(
    () =>
      selectedEmployee && Array.isArray(selectedEmployee.pageAccess)
        ? selectedEmployee.pageAccess
        : [],
    [selectedEmployee]
  );
  const hasChanges = useMemo(() => {
    const a = [...originalGrants].sort();
    const b = [...draftGrants].sort();
    return a.length !== b.length || a.some((v, i) => v !== b[i]);
  }, [originalGrants, draftGrants]);

  const changeCount = useMemo(() => {
    const before = new Set(originalGrants);
    const after = new Set(draftGrants);
    return [...after].filter((path) => !before.has(path)).length + [...before].filter((path) => !after.has(path)).length;
  }, [originalGrants, draftGrants]);

  const resetDraft = () => setDraftGrants([...originalGrants]);

  /** Switches the editor to another employee (or closes it), asking first when the draft is unsaved. */
  const requestSelect = (emp) => {
    if ((emp ? emp.id : null) === selectedId) return;
    if (hasChanges) {
      setPendingSelection({ employee: emp });
      return;
    }
    selectEmployee(emp);
  };

  const confirmPendingSelection = () => {
    if (!pendingSelection) return;
    selectEmployee(pendingSelection.employee);
    setPendingSelection(null);
  };

  const handleSave = async () => {
    if (!selectedEmployee) return;
    setSaving(true);
    try {
      // Persist only meaningful grants: pages not already covered by role or
      // the hardcoded email allow-list. Preserve any paths outside this tool.
      const knownPaths = new Set(allPages.map((p) => p.path));
      const cleanedGrants = draftGrants.filter((path) => {
        const page = allPages.find((p) => p.path === path);
        if (!page) return false;
        const state = pageState(page, [path], selectedEmployee.eMail, selectedRoles);
        return state === 'granted';
      });
      const preserved = (selectedEmployee.pageAccess || []).filter(
        (p) => !knownPaths.has(p)
      );
      const finalGrants = Array.from(new Set([...cleanedGrants, ...preserved]));

      await updateDoc(doc(db, 'Employees', selectedEmployee.id), {
        pageAccess: finalGrants
      });

      setAllEmployees((prev) =>
        prev.map((e) =>
          e.id === selectedEmployee.id ? { ...e, pageAccess: finalGrants } : e
        )
      );
      setDraftGrants(finalGrants);
      toast.success(label('toast.saved', { name: `${selectedEmployee.eName} ${selectedEmployee.eSurname}` }));
    } catch (error) {
      console.error('Error saving page access:', error);
      toast.error(label('toast.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const openPasswordModal = () => {
    setNewPassword('');
    setSendEmail(true);
    setPwError('');
    setShowPasswordModal(true);
  };

  const fillGeneratedPassword = () => setNewPassword(generateStrongPassword());

  const handleSetPassword = async () => {
    if (!selectedEmployee) return;
    setPwError('');

    if (!selectedEmployee.eMail) {
      setPwError(label('password.errors.noEmail'));
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setPwError(label('password.errors.tooShort'));
      return;
    }

    setPwSaving(true);
    try {
      if (!auth.currentUser) {
        throw new Error(label('password.errors.noSession'));
      }
      const idToken = await auth.currentUser.getIdToken();

      const response = await fetch(SET_PASSWORD_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          email: selectedEmployee.eMail,
          newPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || label('password.errors.failed'));
      }

      // İsteğe bağlı: yeni şifreyi e-posta ile gönder
      if (sendEmail) {
        try {
          const mailRes = await fetch(CREDENTIALS_EMAIL_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: selectedEmployee.eMail,
              password: newPassword,
              displayName: `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim(),
              loginUrl: 'https://management.habcarecro.com',
              fromName: 'HabcareCRO Yönetim',
            }),
          });
          if (!mailRes.ok) throw new Error('mail non-OK');
          toast.success(label('toast.passwordMailed'));
        } catch (mailErr) {
          console.error('Şifre e-postası gönderilemedi:', mailErr);
          toast.warn(label('toast.passwordMailFailed'));
        }
      } else {
        toast.success(label('toast.passwordUpdated'));
      }

      setShowPasswordModal(false);
    } catch (error) {
      console.error('Şifre güncelleme hatası:', error);
      setPwError(error.message || label('password.errors.generic'));
    } finally {
      setPwSaving(false);
    }
  };

  // Ek yetkisi olan: bu ekrandan verilmiş, rol / kod kapsamı dışında en az bir sayfa
  const hasExtraGrants = (emp) => {
    const roles = getRoles(emp);
    const grants = Array.isArray(emp.pageAccess) ? emp.pageAccess : [];
    return allPages.some((page) => pageState(page, grants, emp.eMail, roles) === 'granted');
  };

  const grantedCount = useMemo(
    () => employees.filter(hasExtraGrants).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [employees, allPages]
  );

  const filteredEmployees = useMemo(() => {
    const term = employeeSearch.trim().toLowerCase();
    return employees.filter((e) => {
      if (listFilter === 'online' && !onlineUsers.has(e.id)) return false;
      if (listFilter === 'granted' && !hasExtraGrants(e)) return false;
      if (!term) return true;
      return `${e.eName || ''} ${e.eSurname || ''}`.toLowerCase().includes(term) ||
        (e.eMail || '').toLowerCase().includes(term);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, employeeSearch, listFilter, onlineUsers]);

  // Live accessible-page count for the selected employee (using the draft)
  const draftAccessibleCount = useMemo(() => {
    if (!selectedEmployee) return 0;
    return allPages.filter(
      (p) => pageState(p, draftGrants, selectedEmployee.eMail, selectedRoles) !== 'none'
    ).length;
  }, [allPages, draftGrants, selectedEmployee, selectedRoles]);

  return {
    allPages,
    loading,
    employees,
    passiveCount,
    showPassiveEmployees,
    setShowPassiveEmployees,
    adminEmployees,
    restrictedAdminEmployees,
    onlineUsers,
    onlineEmployees,
    onlineActivity,
    employeeSearch,
    setEmployeeSearch,
    listFilter,
    setListFilter,
    grantedCount,
    filteredEmployees,
    accessibleCount,
    selectedEmployee,
    selectedRoles,
    requestSelect,
    selectEmployee,
    pendingSelection,
    setPendingSelection,
    confirmPendingSelection,
    pageSearch,
    setPageSearch,
    pageFilter,
    setPageFilter,
    draftGrants,
    toggleGrant,
    toggleGroup,
    hasChanges,
    changeCount,
    resetDraft,
    saving,
    handleSave,
    draftAccessibleCount,
    messageTarget,
    setMessageTarget,
    openMessage,
    messageText,
    setMessageText,
    sendingMessage,
    sendMessage,
    showPasswordModal,
    setShowPasswordModal,
    openPasswordModal,
    newPassword,
    setNewPassword,
    fillGeneratedPassword,
    sendEmail,
    setSendEmail,
    pwSaving,
    pwError,
    handleSetPassword,
  };
};

export default usePageAccess;
